import * as React from 'react';
import { MultiSelectCheckboxItem } from 'Src/components/MultiSelectCheckboxes/MultiSelectCheckboxes';
import { DatePicker as SitDatePicker, DateRange } from 'Src/components/DatePicker/DatePicker';
import { ClubWizardErrors } from '../ClubWizard';
import { ClubSport } from '../../../../../../../models/club';
import { Sport } from '../../../../../../../models/sport';
import '../ClubWizard.scss';

interface Props {
  termItems: MultiSelectCheckboxItem[];
  dayItems: MultiSelectCheckboxItem[];
  start: Date;
  end: Date;
  repeatEveryWeeks: number;
  duration: number;
  time: string;
  name: string;
  sport: Sport | ClubSport;
  genderItems: MultiSelectCheckboxItem[];
  ageItems: MultiSelectCheckboxItem[];
  onTermsChange: (index: number) => void;
  onStartDateChange: (value: Date) => void;
  onEndDateChange: (value: Date) => void;
  onDaysChange: (index: number) => void;
  onInputChange: (event) => void;
  errors: ClubWizardErrors;
  scheduleMode: 'DATES' | 'TERMS' | 'DATES_LIST';
  onScheduleModeChange: (mode: 'DATES' | 'TERMS' | 'DATES_LIST') => void;
  specificDates: Date[];
  onSpecificDatesChange: (dates: Date[]) => void;
}

const renderChip = (item: MultiSelectCheckboxItem, index: number, onChange: (index: number) => void) => (
  <button
    key={`${item.name}_${index}`}
    type="button"
    className={`clubWizardDsChip ${item.isChecked ? 'isSelected' : ''}`}
    onClick={() => onChange(index)}
  >
    {item.isChecked && <span>✓</span>}
    {item.name}
  </button>
);

const renderTerm = (item: MultiSelectCheckboxItem, index: number, onChange: (index: number) => void) => (
  <button
    key={`${item.name}_${index}`}
    type="button"
    className={`clubWizardDsTermOption ${item.isChecked ? 'isSelected' : ''}`}
    onClick={() => onChange(index)}
  >
    <span>{item.isChecked ? '✓' : ''}</span>
    <strong>{item.name}</strong>
  </button>
);

export function ClubWizardScheduleStep(props: Props) {
  const {
    termItems,
    onTermsChange,
    start,
    onStartDateChange,
    onEndDateChange,
    end,
    dayItems,
    onDaysChange,
    onInputChange,
    repeatEveryWeeks,
    duration,
    time,
    errors,
    scheduleMode,
    onScheduleModeChange,
    specificDates,
    onSpecificDatesChange
  } = props;

  const indexedTerms = termItems.map((item, index) => ({ item, index }));
  const breaks = indexedTerms.filter(({ item }) => /break|holiday|half[ -]?term/i.test(item.name));
  const teachingTerms = indexedTerms.filter(({ item }) => !/break|holiday|half[ -]?term/i.test(item.name));
  const [startHour = 0, startMinute = 0] = String(time || '00:00')
    .split(':')
    .map(value => Number(value));
  const startTimeValue = new Date(2000, 0, 1, startHour, startMinute, 0, 0);
  return (
    <div className="clubWizardDsStep">
      <section className="clubWizardDsSection">
        <div className="clubWizardDsSection__head">
          <span className="clubWizardDsSection__icon">1</span>
          <div>
            <h3>Schedule</h3>
            <p>Set the date range or terms, then choose when sessions run.</p>
          </div>
        </div>

        <div className="clubWizardDsField">
          <span>How is the run defined?</span>
          <div className="clubWizardDsSegmented">
            <button
              type="button"
              className={scheduleMode === 'DATES' ? 'isSelected' : ''}
              onClick={() => onScheduleModeChange('DATES')}
            >
              Date range
            </button>
            <button
              type="button"
              className={scheduleMode === 'TERMS' ? 'isSelected' : ''}
              onClick={() => onScheduleModeChange('TERMS')}
            >
              School terms
            </button>
            <button
              type="button"
              className={scheduleMode === 'DATES_LIST' ? 'isSelected' : ''}
              onClick={() => onScheduleModeChange('DATES_LIST')}
            >
              Specific days
            </button>
          </div>
          <small>
            {scheduleMode === 'TERMS'
              ? 'Sessions run across the terms selected below.'
              : scheduleMode === 'DATES_LIST'
              ? 'Sessions run only on the individual dates selected below.'
              : 'Sessions run between the start and finish dates.'}
          </small>
        </div>

        {scheduleMode === 'DATES' ? (
          <div className="clubWizardDsField clubWizardDsDateRange">
            <span>Date range</span>
            <SitDatePicker
              mode="range"
              value={{ start, end }}
              onChange={value => {
                if (!value || value instanceof Date) return;
                const range = value as DateRange;
                if (range.start) onStartDateChange(new Date(range.start));
                if (range.end) onEndDateChange(new Date(range.end));
              }}
              format="DD-MM-YYYY"
              placeholder="Select club date range"
              numberOfMonths={2}
              syncRangeOnSelect={true}
              className="clubWizardDsDateRange__picker"
            />
            <small>Choose the first and last date on which this club runs.</small>
            {errors.date && <em>Club finish date cannot be earlier than the start date.</em>}
            {errors.interval && <em>Start and finish dates are required.</em>}
          </div>
        ) : scheduleMode === 'TERMS' ? (
          <div className="clubWizardDsField">
            <span>School terms</span>
            <div className="clubWizardDsTermGroups">
              <div>
                <strong>Terms</strong>
                <div className="clubWizardDsTermChips">
                  {teachingTerms.map(({ item, index }) => renderTerm(item, index, onTermsChange))}
                </div>
              </div>
              {breaks.length > 0 && (
                <div>
                  <strong>Breaks and holidays</strong>
                  <div className="clubWizardDsTermChips">
                    {breaks.map(({ item, index }) => renderTerm(item, index, onTermsChange))}
                  </div>
                </div>
              )}
            </div>
            <small>Select one or more records from School terms.</small>
            {errors.interval && <em>Select at least one school term.</em>}
          </div>
        ) : (
          <div className="clubWizardDsField clubWizardDsSpecificDates">
            <span>Add specific dates</span>
            <SitDatePicker
              mode="single"
              value={null}
              onChange={value => {
                if (!(value instanceof Date)) return;
                const normalized = new Date(value);
                normalized.setHours(0, 0, 0, 0);
                const exists = specificDates.some(date => new Date(date).setHours(0, 0, 0, 0) === normalized.getTime());
                if (exists) return;
                onSpecificDatesChange(
                  [...specificDates, normalized].sort((left, right) => left.getTime() - right.getTime())
                );
              }}
              format="DD-MM-YYYY"
              placeholder="Choose a date to add"
              clearable={false}
              className="clubWizardDsSpecificDates__picker"
            />
            {specificDates.length > 0 ? (
              <div className="clubWizardDsSpecificDates__list">
                {specificDates.map(date => (
                  <span key={date.toISOString()}>
                    {date.toLocaleDateString('en-GB')}
                    <button
                      type="button"
                      aria-label={`Remove ${date.toLocaleDateString('en-GB')}`}
                      onClick={() =>
                        onSpecificDatesChange(specificDates.filter(item => item.getTime() !== date.getTime()))
                      }
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <small>No dates selected yet.</small>
            )}
            {errors.interval && <em>Add at least one specific date.</em>}
          </div>
        )}

        <div className="clubWizardDsDivider" />
        <div className="clubWizardDsSection__head">
          <span className="clubWizardDsSection__icon">2</span>
          <div>
            <h3>Sessions</h3>
            <p>Choose the session days, frequency, start time and duration.</p>
          </div>
        </div>

        {scheduleMode !== 'DATES_LIST' && (
          <div className="clubWizardDsField">
            <span>Days of the week</span>
            <div className="clubWizardDsChipRow">
              {dayItems.map((item, index) => renderChip(item, index, onDaysChange))}
            </div>
            {errors.days && <em>Required</em>}
          </div>
        )}

        <div
          className={`clubWizardDsGrid ${
            scheduleMode === 'DATES_LIST' ? 'clubWizardDsGrid--two' : 'clubWizardDsGrid--three'
          }`}
        >
          {scheduleMode !== 'DATES_LIST' && (
            <label className="clubWizardDsField">
              <span>Repeat every</span>
              <div className="clubWizardDsAffixField">
                <input type="text" onChange={onInputChange} value={repeatEveryWeeks} name="repeatEveryWeeks" />
                <b>week(s)</b>
              </div>
            </label>
          )}

          <label className="clubWizardDsField">
            <span>Start time</span>
            <SitDatePicker
              mode="time"
              value={startTimeValue}
              onChange={value => {
                if (!(value instanceof Date)) return;
                const hours = value.getHours();
                const minutes = value.getMinutes();
                const nextTime = `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`;
                onInputChange({ preventDefault: () => undefined, target: { name: 'time', value: nextTime } });
              }}
              format="HH:mm"
              minuteStep={5}
              clearable={false}
              placeholder="Select start time"
              className="clubWizardDsTimePicker"
            />
          </label>

          <label className="clubWizardDsField">
            <span>Duration</span>
            <div className="clubWizardDsAffixField">
              <input type="text" onChange={onInputChange} value={duration} name="duration" />
              <b>min</b>
            </div>
            {errors.duration && <em>Required</em>}
          </label>
        </div>
      </section>
    </div>
  );
}
